
import Vue, { PropType } from 'vue';
import _keyBy from 'lodash/keyBy';
import { CompanyUserRoleDef } from '@run-diffusion/shared';
import { ROUTER } from '@/router/constants';
import { mapGetters, mapState } from 'vuex';
import { companyUserRoleTargetRowFromMembership } from '@/views/Team/CompanyUserRoleDialogs/companyUserRoleDialogState';
import TeamMemberMetricsBtns from '@/views/Team/TeamMemberDashboard/TeamMemberMetricsBtns.vue';
import MemberCompanyRoleTableCell from '@/views/Team/MembersDirectory/MemberCompanyRoleTableCell.vue';
import BackButton from '@/components/base/BackButton.vue';
import BaseBreadcrumbs from '@/components/base/BaseBreadcrumbs.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import BaseStyledMenu from '@/components/base/BaseStyledMenu.vue';
import { TeamUserMixin } from '@/mixins/TeamUserMixin';

export default Vue.extend({
	name: 'TeamMemberOverview',
	props: {
		currTeam: { type: Object, default: null },
		currTeamMember: { type: Object, default: null },
		// How many active Teams this member has joined, within what the viewer may see; null until known
		joinedTeamsCount: { type: Number, default: null },
		loadingJoinedTeamsCount: { type: Boolean, default: false },
		companyUserRoleDefs: { type: Array as PropType<CompanyUserRoleDef[]>, default: () => [] },
	},
	mixins: [
		TeamUserMixin,
	],
	data () {
		return {
			// What the company-role cell resolved live: undefined until it reports.
			liveCompanyRoleDefId: undefined as string | null | undefined,

			COMPANY_ROLE_ACTION_VALUE: {
				ADD: 'add_company_role',
				EDIT: 'edit_company_role',
				REMOVE: 'remove_company_role',
			},
		};
	},
	computed: {
		...mapState([
			'user',
		]),
		...mapGetters([
			'displayLabels',
		]),
		breadcrumbs () {
			return [
				{
					hoverColor: 'var(--primary-purple)',
					text: 'Company Dashboard',
					disabled: false,
					to: {
						name: ROUTER.COMPANY_DASHBOARD,
						query: this.$route.query,
					},
				},
				{
					hoverColor: 'var(--primary-blue)',
					text: this.currTeam?.name || `${this.displayLabels.team} Dashboard`,
					disabled: false,
					to: {
						name: ROUTER.TEAM_DASHBOARD,
						params: {
							teamId: this.$route.params.teamId,
						},
						query: this.$route.query,
					},
				},
				{
					hoverColor: 'var(--primary-pink)',
					text: this.currTeamMember?.email || 'Member Dashboard',
					disabled: true,
					to: {
						name: ROUTER.TEAM_MEMBER_DASHBOARD,
						params: {
							teamId: this.$route.params.teamId,
							teamMemberId: this.$route.params.teamMemberId,
						},
						query: this.$route.query,
					},
				},
			];
		},
		companyId (): string | null {
			return this.currTeam?.companyId || null;
		},
		companyUserRoleDefsMap (): Record<string, CompanyUserRoleDef> {
			return _keyBy(this.companyUserRoleDefs || [], 'id');
		},
		memberUserId (): string | null {
			return this.currTeamMember?.userId || null;
		},
		// The company role a pending invite will grant on acceptance.
		// manageCompanyAdmin stamps it on every pending invite the person has on an
		// active team, so this team's copy speaks for them all.
		stubCompanyUserRoleDefId (): string | null {
			if (!this.currTeamMember || this.currTeamMember.isAccepted) return null;
			return this.currTeamMember.companyUserRoleDefId || null;
		},
		companyUserRoleDefId (): string | null {
			if (this.liveCompanyRoleDefId !== undefined) return this.liveCompanyRoleDefId;
			return this.stubCompanyUserRoleDefId;
		},
		isSelfMember (): boolean {
			return !!(this.user?.id && this.memberUserId && this.user.id === this.memberUserId);
		},
		// Same gate as the directory row: the company permission, never a
		// per-team one, and never on an IdP-managed company (the SCIM sync
		// reverts company-role edits).
		canManageCompanyRole (): boolean {
			return !!(
				this.hasManageCompanyPermission &&
				!this.scimProvisioningLocksMembership &&
				(this.user?.isAdmin || !this.isSelfMember)
			);
		},
		companyRoleMenuItems () {
			if (!this.canManageCompanyRole) return [];
			// An account's role is only known once the cell has bound it. Until
			// then Add could be offered to someone who already holds a role.
			if (this.memberUserId && this.liveCompanyRoleDefId === undefined) return [];
			let idIncrementer: number = 0;
			return this.companyUserRoleDefId ? [
				{
					id: idIncrementer++,
					icon: 'mdi-shield-account-outline',
					label: 'Update Company Role',
					description: 'Change their company role',
					value: this.COMPANY_ROLE_ACTION_VALUE.EDIT,
				},
				{
					id: idIncrementer++,
					icon: 'mdi-minus',
					iconStyle: { color: 'var(--red-500)' },
					descriptionStyle: { color: 'var(--grey-400)' },
					label: 'Remove Company Role',
					description: 'They will lose company admin access',
					value: this.COMPANY_ROLE_ACTION_VALUE.REMOVE,
				},
			] : [
				{
					id: idIncrementer++,
					icon: 'mdi-shield-plus-outline',
					label: 'Assign Company Role',
					description: 'Grant them a company role',
					value: this.COMPANY_ROLE_ACTION_VALUE.ADD,
				},
			];
		},
	},
	watch: {
		// The cell is keyed by the membership and reports nothing on its way
		// out, so the last person's answer is forgotten here.
		'currTeamMember.id' () {
			this.liveCompanyRoleDefId = undefined;
		},
	},
	methods: {
		onBoundRoleDefId (roleDefId: string | null | undefined) {
			this.liveCompanyRoleDefId = roleDefId;
		},
		onSelectedCompanyRoleAction (actionValue: string) {
			const payload = {
				row: companyUserRoleTargetRowFromMembership(this.currTeamMember, this.currTeam),
				email: this.currTeamMember?.email || '',
				userId: this.memberUserId,
				companyUserRoleDefId: this.companyUserRoleDefId,
			};

			// The same three events the directory row emits; TeamPage owns the dialogs.
			switch (actionValue) {
				case this.COMPANY_ROLE_ACTION_VALUE.ADD:
					this.$emit('add-company-user-role', payload);
					break;
				case this.COMPANY_ROLE_ACTION_VALUE.EDIT:
					this.$emit('edit-company-user-role', payload);
					break;
				case this.COMPANY_ROLE_ACTION_VALUE.REMOVE:
					this.$emit('remove-company-user-role', payload);
					break;
				default:
					break;
			}
		},
		goBack () {
			// Go through breadcrumbs in reverse order to find the back route
			for (let i = this.breadcrumbs.length - 1; i >= 0; i--) {
				const breadcrumb = this.breadcrumbs[i];
				if (breadcrumb.to && !breadcrumb.disabled) {
					this.routerPush(this.$route, this.$router, breadcrumb.to);
					return;
				}
			}
		},
	},
	components: {
		TeamMemberMetricsBtns,
		MemberCompanyRoleTableCell,
		BackButton,
		BaseBreadcrumbs,
		BaseButton,
		BaseStyledMenu,
	},
});
